import React, { useState } from 'react';
import { Home, X, ImagePlus, ShoppingBag, Loader2, Check } from 'lucide-react';
import { RoomMockup } from './RoomMockup';
import { ROOM_SCENES } from './roomScenes';

/* Compact card showing a saved Room Preview look (used on the quote page) */
export const LookCard = ({ look, onRemove, onAddToQuote }) => {
  const [width, setWidth] = useState(36);
  const [height, setHeight] = useState(60);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  if (!look) return null;
  const sizeOk = width >= 12 && width <= 144 && height >= 12 && height <= 120;

  const add = async () => {
    setAdding(true);
    try {
      await onAddToQuote({ width: Number(width), height: Number(height) });
      setAdded(true);
    } finally { setAdding(false); }
  };
  const scene = look.custom_scene
    ? { id: 'custom', name: 'Your Room', icon: ImagePlus, aspect: look.custom_scene.aspect, images: { day: look.custom_scene.imageUrl }, shades: [{ quad: look.custom_scene.quad }] }
    : ROOM_SCENES.find(s => s.id === look.room_id) || ROOM_SCENES[0];
  const light = { mode: look.light_mode, label: look.light_label };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="quote-look-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-900">
          <Home className="w-4 h-4 text-amber-600" /> Your selected look
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} className="p-1 rounded-full hover:bg-neutral-100" aria-label="Remove look" data-testid="quote-look-remove">
            <X className="w-4 h-4 text-neutral-400" />
          </button>
        )}
      </div>
      <RoomMockup scene={scene} ambience={look.ambience || 'day'} textureUrl={look.texture_url} light={light} filterOpacity={look.filter_opacity} openness={look.openness} drop={look.drop} animate={false} />
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg overflow-hidden border border-neutral-200 flex-shrink-0">
          <img src={look.texture_url} alt={look.fabric_name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-900 truncate">{look.fabric_name} <span className="text-neutral-400 font-normal">{look.fabric_code}</span></p>
          <p className="text-xs text-neutral-500 truncate">
            {[look.material_name, look.collection_name].filter(Boolean).join(' · ')} — {look.light_label}{look.light_mode === 'screen' ? ` ${look.openness}%` : ''} · {look.room_name}{look.ambience === 'night' ? ' (night)' : ''} · {Math.round((look.drop ?? 0.5) * 100)}% lowered
          </p>
        </div>
      </div>
      {onAddToQuote && (
        <div className="px-4 pb-4 flex flex-wrap items-end gap-3 border-t border-neutral-100 pt-3" data-testid="look-add-to-quote">
          <label className="text-xs text-neutral-600">
            Width (in)
            <input type="number" min="12" max="144" value={width} onChange={e => setWidth(e.target.value)} className="mt-1 block w-24 rounded-md border border-neutral-300 px-2 py-1.5 text-sm" data-testid="look-width-input" />
          </label>
          <label className="text-xs text-neutral-600">
            Height (in)
            <input type="number" min="12" max="120" value={height} onChange={e => setHeight(e.target.value)} className="mt-1 block w-24 rounded-md border border-neutral-300 px-2 py-1.5 text-sm" data-testid="look-height-input" />
          </label>
          <button
            type="button"
            onClick={add}
            disabled={adding || added || !sizeOk}
            className={`ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${added ? 'bg-emerald-600 text-white' : 'bg-neutral-900 text-white hover:bg-neutral-800'} disabled:opacity-60`}
            data-testid="look-add-to-quote-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : added ? <Check className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
            {added ? 'Added to quote' : 'Add roller shade to quote'}
          </button>
          {!sizeOk && <p className="w-full text-[11px] text-red-600">Width 12–144 in, height 12–120 in.</p>}
        </div>
      )}
    </div>
  );
};

export default LookCard;
